import styled from "styled-components";
import { Container } from "../../components/Container/Container";
import { Body, Headline2 } from "../../components/Text/Text";
import { useActionState, useEffect, useState } from "react";
import { ButtonLink } from "../../components/ButtonLink/ButtonLink";
import { Spinner } from "../../components/Spinner/Spinner";
import { submitPasswordResetForm } from "../../actions/submit-password-reset-form";
import { Button } from "../../components/Button/Button";

const Background = styled.div`
    background-color: var(--color-light);
`;

const StyledContainer = styled(Container)`
    padding-top: var(--menu-height);
`;

const StyledHeadline2 = styled(Headline2)`
    text-transform: uppercase;
    margin-bottom: 20px;
`;

const Flex = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    max-width: 544px;
    
    margin: 0 auto;
    padding-top: 150px;
    padding-bottom: 100px;

    @media screen and (max-width: 1024px) {
        padding-top: 80px;
        padding-bottom: 60px;
    }

    @media screen and (max-width: 475px) {
        padding-top: 40px;
        padding-bottom: 40px;
    }
`;

const StyledBody = styled(Body)`
    white-space: pre-line;
`;

const FormBox = styled.div`
    width: 100%;
    flex: 1;

    margin-top: 60px;
    
    ${ButtonLink} {
        display: block;
        margin-left: auto;
    }
`;

const Input = styled.input`
    font-family: var(--font-poppins);
    font-size: var(--fs-form);
    line-height: 1.55;
    padding: 12px 18px;
    width: 100%;
    background-color: var(--color-white);
    border: 1px solid ${props => props?.$error ? 'var(--color-error)' : 'var(--color-label-stroke)'};
    outline: none;
    border-radius: 8px;
    max-height: 50px;

    &::placeholder {
        color: var(--color-light-400);
    }

    &:focus {
        border: 1px solid var(--color-green);
    }
`;

const Label = styled(Body).attrs({
    as: 'label'
})`
    font-weight: var(--fw-medium);
    font-size: var(--fs-form);
`;

const Required = styled.span`
    color: var(--color-green);
`;

const FormRow = styled.div`
    margin-bottom: 24px;
    
    &:last-of-type {
        margin-bottom: 36px;
    }
`;

const Error = styled(Body).attrs({
    $small: true,
})`
    color: var(--color-error);
    margin-top: 5px;
    margin-bottom: 24px;
    text-wrap-style: balance;
    text-align: right;
    font-size: var(--fs-error);
`;

const ButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 12px;
`;

const Wrapper = styled.div`
    position: relative;
    width: fit-content;
`

const SubmitButton = styled(Button)`
    position: relative;
    pointer-events: ${props => props.$loading ? 'none' : 'auto'};
    opacity: ${props => props.$loading ? 0.5 : 1};
`;

const StyledSpinner = styled(Spinner)`
    position: absolute;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: scale(0.5);
    z-index: 1;
`

const initialState = { ok: null, message: null, errors: {} };

function ResetPassword() {

    const [state, submitAction, isPending] = useActionState(submitPasswordResetForm, initialState);
    const [emailSent, setEmailSent] = useState(false);

    useEffect(() => {
        if (state?.ok) setEmailSent(true);
    }, [state?.ok])

    if (emailSent) return (
        <>
            <title>Wachtwoord resetten &mdash; Jurist & Werk Congres</title>

            <Background>
                <StyledContainer>
                    <Flex>
                        <StyledHeadline2 $noTransform>Wachtwoord resetten</StyledHeadline2>
                        <StyledBody $small>
                            De aanvraag om je wachtwoord te wijzigen is verstuurd. We sturen je een e-mail als het adres bij ons bekend is.
                        </StyledBody>

                        <div style={{ marginTop: '24px'}}>
                            <ButtonLink to="/inloggen" type="button" $green>
                                Terug naar inloggen
                            </ButtonLink>
                        </div>
                    </Flex>
                </StyledContainer>
            </Background>
        </>
    )

    return (
        <>
            <title>Wachtwoord resetten &mdash; Jurist & Werk Congres</title>

            <Background>
                <StyledContainer>

                    <Flex>

                        <StyledHeadline2 $noTransform>Wachtwoord resetten</StyledHeadline2>
                        <StyledBody $small>
                            Vul hier je e-mailadres in. Als het adres bij ons bekend is, sturen we je een link om je wachtwoord te wijzigen.
                        </StyledBody>

                        <FormBox>
                            <form action={submitAction}>
                                <FormRow>
                                    <Label htmlfFor="email">E-mailadres<Required aria-label="required">*</Required></Label>
                                    <Input id="email" type="text" name="email" placeholder="E-mailadres" $error={state?.errors?.email} />
                                    {state?.errors?.Email && <Error>{state?.errors?.Email}</Error>}
                                </FormRow>

                                {!state?.ok && state?.message && <Error>{state?.message}</Error>}

                                <ButtonWrapper>
                                    <ButtonLink to="/inloggen" type="button" $green>
                                        Terug
                                    </ButtonLink>
                                    <Wrapper>
                                        {isPending && <StyledSpinner $small $absolute /> }
                                        <SubmitButton as="button" type="submit" $loading={isPending}>
                                            Verstuur
                                        </SubmitButton>
                                    </Wrapper>
                                </ButtonWrapper>
                            </form>
                        </FormBox>
                    </Flex>
                </StyledContainer>
            </Background>
        </>
    )
}

export default ResetPassword;